'use client'

/**
 * Legacy-port shim for the legacy `client/src/lib/queryClient.ts`.
 *
 * Differences from legacy:
 *   - No Bearer/JWT (Supabase cookie session does auth)
 *   - 401 redirects to /login (matches unified routes), not legacy /login
 *   - `credentials: 'include'` on every fetch so the cookie travels
 */

import { QueryClient, type QueryFunction } from '@tanstack/react-query'

function normalizeApiPath(path: string): string {
  if (!path) return '/'
  return path.startsWith('/') ? path : `/${path}`
}

let isHandlingLogout = false

function handle401() {
  if (typeof window === 'undefined') return
  if (isHandlingLogout) return
  if (window.location.pathname.startsWith('/login')) return
  isHandlingLogout = true
  queryClient.clear()
  // Full document navigation so in-memory query + auth client state cannot survive a 401.
  // eslint-disable-next-line @next/next/no-location-assign-relative-destination
  window.location.href = '/login'
}

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText
    if (res.status === 401) {
      handle401()
      throw new Error(`401: ${text}`)
    }
    throw new Error(`${res.status}: ${text}`)
  }
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown,
): Promise<Response> {
  const fullUrl = normalizeApiPath(url)
  const headers: Record<string, string> = {}
  if (data !== undefined) headers['Content-Type'] = 'application/json'

  const res = await fetch(fullUrl, {
    method,
    headers,
    body: data !== undefined ? JSON.stringify(data) : undefined,
    credentials: 'include',
  })

  await throwIfResNotOk(res)
  return res
}

type UnauthorizedBehavior = 'returnNull' | 'throw'

export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const path = (queryKey as readonly unknown[]).join('/') as string
    const fullUrl = normalizeApiPath(path)

    const res = await fetch(fullUrl, {
      credentials: 'include',
    })

    if (unauthorizedBehavior === 'returnNull' && res.status === 401) {
      return null as never
    }

    await throwIfResNotOk(res)
    return (await res.json()) as never
  }

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: 'throw' }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
})
